import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { cn } from '@/lib/utils';
import { useCssDimensions } from '@/utils/hooks/useCssDimensions';

import { ImageLink } from '@/components/atoms/ImageLink';
import { LocalImage } from '@/components/atoms/LocalImage';
import { IconsLinksList } from '@/components/molecules/IconsLinksList';
import { MobileNavigationList } from '@/components/molecules/MobileNavigationList';

import type { FooterConfigType } from '../../Footer.types';

interface FooterMobileProps
    extends
        Omit<FooterConfigType, 'footerInfo' | 'promoCodesModal'>,
        HTMLAttributes<HTMLDivElement> {
    theme: string | undefined;
    location: string;
}

const FooterMobile = forwardRef<HTMLDivElement, FooterMobileProps>(
    ({ theme, links, location, className }, ref) => {
        const { minWidthLg } = useCssDimensions();

        const appLinks = links.find(link => link.appLinks)?.appLinks;

        return (
            <div
                ref={ref}
                className={cn(
                    'border-t border-grey-100 pb-12 pt-12',
                    className,
                )}
            >
                <div className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-6">
                    <LocalImage
                        src={
                            theme === 'dark'
                                ? '/images/logos/logo-white.svg'
                                : '/images/logos/logo.svg'
                        }
                        alt="PayPo"
                        width={85}
                        height={23}
                        className="flex-none"
                    />

                    {!!links[0]?.socialLinks && !minWidthLg && (
                        <IconsLinksList
                            variant="horizontalLeft"
                            iconsData={links[0].socialLinks}
                            className="ml-auto lg:hidden"
                        />
                    )}

                    {!!links[0]?.partnersLogo && (
                        <div className="flex min-w-[280px] flex-1 items-center justify-between gap-x-4 min-[530px]:ml-auto min-[530px]:flex-none min-[530px]:justify-end min-[530px]:gap-x-6">
                            {links[0].partnersLogo.map(logo => (
                                <ImageLink
                                    key={logo.src}
                                    {...logo}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <MobileNavigationList
                    links={links}
                    location={location}
                    navigationLinkVariant={{
                        color: 'secondary',
                        weight: 'normal',
                        states: 'enable',
                        className: 'text-2xs',
                    }}
                    accordionClassName="text-sm"
                    navigationLinkClassName="pb-4"
                    className="w-full"
                />

                {!!appLinks && (
                    <IconsLinksList
                        variant="vertical"
                        iconsData={appLinks}
                        className="mt-8 justify-start lg:justify-center"
                    />
                )}
            </div>
        );
    },
);

export default FooterMobile;
